/**
 * Video Redux Slice
 * 
 * Centralized state management for video initialization and playback.
 * Prevents component re-renders by storing video data at app level.
 * 
 * Each video is identified by its videoKey and stored with:
 * - Decryption keys (from cloud function)
 * - Video URL (with CDN token)
 * - Loading state
 * - Error state
 * 
 * Note: HLS instances are managed separately in VideoPlayerContext
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { VideoDecryptionKeys } from '@/domain/entities/video/videoDecryptionEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { createInitializeVideoUseCase } from '@/domain/usecases/video/initializeVideoUseCase';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { selectHas4kAccess } from '@/presentation/store/slices/premiumPackages/premiumPackagesSlice';
import type { RootState } from '@/presentation/store';

export interface VideoState {
  videoKey: string;
  decryptionKeys: VideoDecryptionKeys;
  videoUrl: string;
  isLoading: boolean;
  error: string | null;
  initializedAt: number; // Timestamp for cache invalidation
  isDisposed: boolean; // Whether video has been disposed and should be removed from DOM
}

export interface VideosState {
  videos: Record<string, VideoState>; // Map of videoKey -> VideoState
}

const initialState: VideosState = {
  videos: {},
};

/**
 * Async thunk to initialize a video
 * Fetches decryption keys and builds video URL
 */
export const initializeVideo = createAsyncThunk(
  'video/initialize',
  async (
    {
      media,
      videoKey
    }: {
      media?: UserMediaEntity;
      videoKey: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Read the 4K-access gate from Redux at the dispatch boundary: full-quality
      // (`master.m3u8`) only when the user has 4K access, otherwise the light
      // (`master_light.m3u8`) manifest. Uses the shared `selectHas4kAccess`
      // (`hasPremiumAction(access4k)`) for consistency with the rest of the app.
      const has4kAccess = selectHas4kAccess(getState() as RootState);

      // Get Firebase user for UID and IAT
      const firebaseUser = getAuthFirebaseUserUseCase.execute();
      if (!firebaseUser) {
        throw new Error('Firebase user not found');
      }

      // Get the video ID from media
      const videoId = media?.id ?? '';
      if (!videoId) {
        throw new Error('Media entity with valid video ID is required');
      }

      // Get the ID token and extract the iat claim
      const idTokenResult = await firebaseUser.getIdTokenResult();
      const iat = Math.floor(new Date(idTokenResult.issuedAtTime).getTime() / 1000);
      const uid = firebaseUser.uid;

      const initializeVideoUseCase = createInitializeVideoUseCase();
      const result = await initializeVideoUseCase.execute({
        videoId,
        uid,
        iat
      }, media, has4kAccess); // Pass media entity for URL construction in case of Media CDN video

      if (!result.success || !result.decryptionEntity || !result.videoUrl) {
        throw result.error || new Error('Failed to initialize video');
      }

      return {
        videoKey,
        decryptionKeys: result.decryptionEntity.keys,
        videoUrl: result.videoUrl,
        initializedAt: Date.now(),
      };
    } catch (error) {
      appLogger.error('Error initializing video', 'VideoSlice.initializeVideo', { error: error as Error });
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }
);

const videoSlice = createSlice({
  name: 'video',
  initialState,
  reducers: {
    /**
     * Inject an ALREADY-resolved video (url + decryption keys) directly into the
     * slice, bypassing the user-gen `initializeVideo` thunk.
     *
     * Used by media surfaces that resolve their own keys/url through a DIFFERENT
     * pipeline than the user-gen `InitializeVideoUseCase` — e.g. encrypted chat
     * media (`InitializeChatVideoUseCase`) and, later, Community group-room video.
     * Those flows own their callable + secret-name prefixes + URL building in
     * their own feature hook; this reducer just lands the result so the shared
     * `VideoComponent` / `VideoProgressBar` / registry machinery lights up exactly
     * as it does for user-gen content. `initializedAt` is passed in by the caller
     * to keep the reducer pure.
     */
    setInitializedVideo: (
      state,
      action: PayloadAction<{
        videoKey: string;
        videoUrl: string;
        decryptionKeys: VideoDecryptionKeys;
        initializedAt: number;
      }>,
    ) => {
      const { videoKey, videoUrl, decryptionKeys, initializedAt } = action.payload;
      state.videos[videoKey] = {
        videoKey,
        decryptionKeys,
        videoUrl,
        isLoading: false,
        error: null,
        initializedAt,
        isDisposed: false,
      };
    },
    /**
     * Clear video data and mark as disposed
     * This signals VideoComponent to remove the video from DOM
     */
    clearVideo: (state, action: PayloadAction<string>) => {
      const videoKey = action.payload;
      if (state.videos[videoKey]) {
        // console.log(`🗑️ [videoSlice] Marking video ${videoKey} as disposed`);
        state.videos[videoKey].isDisposed = true;
      }
    },
    /**
     * Clear all videos (e.g., on logout)
     */
    clearAllVideos: (state) => {
      state.videos = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize video - pending
      .addCase(initializeVideo.pending, (state, action) => {
        const { videoKey } = action.meta.arg;
        state.videos[videoKey] = {
          videoKey,
          decryptionKeys: {
            decryptionKey: new Uint8Array(),
            ivKey: new Uint8Array()
          },
          videoUrl: '',
          isLoading: true,
          error: null,
          initializedAt: 0,
          isDisposed: false,
        };
      })
      // Initialize video - fulfilled
      .addCase(initializeVideo.fulfilled, (state, action) => {
        const { videoKey, decryptionKeys, videoUrl, initializedAt } = action.payload;
        state.videos[videoKey] = {
          videoKey,
          decryptionKeys,
          videoUrl,
          isLoading: false,
          error: null,
          initializedAt,
          isDisposed: false,
        };
      })
      // Initialize video - rejected
      .addCase(initializeVideo.rejected, (state, action) => {
        const { videoKey } = action.meta.arg;
        if (state.videos[videoKey]) {
          state.videos[videoKey].isLoading = false;
          state.videos[videoKey].error = action.payload as string;
        }
      });
  },
});

export const { setInitializedVideo, clearVideo, clearAllVideos } = videoSlice.actions;
export default videoSlice.reducer;
